import React from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    LinearProgress,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AppDatePicker from "../../shared/AppDatePicker";
import ContextTooltip from "../../shared/ContextTooltip";
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
} from "../../shared/customerSelectUtils";
import Autocomplete from "@mui/material/Autocomplete";

const IncomeDetailsDialog = ({
    open,
    onClose,
    onSave,
    detailSaving,
    detailIncome,
    editIncome,
    editErrors,
    onUpdateEditIncome,
    onSetEditIncome,
    onClearEditError,
    resolveEuNetAmount,
    fmtEUR,
    formatDate,
    customerOptions,
    loadingCustomers,
    editCustomerOption,
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
    >
        <DialogTitle>Einnahme - Details</DialogTitle>
        <DialogContent dividers>
            {detailIncome && editIncome ? (
                <Stack spacing={2}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        <strong>Datei:</strong> {detailIncome.originalFilename || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        <strong>Hochgeladen:</strong> {formatDate(detailIncome.uploadDate)}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={Boolean(editIncome.isEuService)}
                                onChange={(e) => {
                                    onUpdateEditIncome({ isEuService: e.target.checked }, "isEuService");
                                    onClearEditError("netAmount");
                                }}
                            />
                        }
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Leistung an EU / EU-Ausland (Reverse Charge)</span>
                                <ContextTooltip
                                    title="EU-Leistung"
                                    description="Bei EU-Leistungen wird die Einnahme netto als Betriebseinnahme erfasst. USt wird in der USt-Auswertung beruecksichtigt."
                                />
                            </Stack>
                        }
                    />
                    {editIncome.isEuService && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            <strong>Netto verbucht:</strong> {fmtEUR(resolveEuNetAmount(editIncome) ?? 0)}
                        </Typography>
                    )}
                    <Stack spacing={3}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Belegdaten
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} sm={6}>
                                    <AppDatePicker
                                        label="Belegdatum"
                                        value={editIncome.invoiceDate || ""}
                                        onChange={(date) => {
                                            onSetEditIncome({ ...editIncome, invoiceDate: date });
                                            onClearEditError("invoiceDate");
                                        }}
                                        error={Boolean(editErrors.invoiceDate)}
                                        helperText={editErrors.invoiceDate || ""}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={6}>
                                    <TextField
                                        label="Belegnummer/Anzeigename"
                                        value={editIncome.invoiceNumber || ""}
                                        onChange={(e) => {
                                            onSetEditIncome({ ...editIncome, invoiceNumber: e.target.value });
                                            onClearEditError("invoiceNumber");
                                        }}
                                        error={Boolean(editErrors.invoiceNumber)}
                                        helperText={editErrors.invoiceNumber || ""}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={12}>
                                    <Autocomplete
                                        options={customerOptions}
                                        loading={loadingCustomers}
                                        value={editCustomerOption}
                                        onChange={(_, value) => {
                                            onSetEditIncome({
                                                ...editIncome,
                                                customerId: value?.id || "",
                                                customerName: value?.displayName || value?.label || "",
                                            });
                                            onClearEditError("customerName");
                                        }}
                                        getOptionLabel={getCustomerOptionLabel}
                                        isOptionEqualToValue={isCustomerOptionEqualToValue}
                                        loadingText={CUSTOMER_SELECT_LOADING}
                                        noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={CUSTOMER_SELECT_LABEL}
                                                placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                error={Boolean(editErrors.customerName)}
                                                helperText={
                                                    editErrors.customerName ||
                                                    (!loadingCustomers && customerOptions.length === 0
                                                        ? CUSTOMER_SELECT_EMPTY_HELPER
                                                        : "")
                                                }
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCustomers ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        fullWidth
                                        sx={{ minWidth: 240 }}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <TextField
                                        label="Beschreibung"
                                        value={editIncome.description || ""}
                                        onChange={(e) => {
                                            onSetEditIncome({ ...editIncome, description: e.target.value });
                                            onClearEditError("description");
                                        }}
                                        error={Boolean(editErrors.description)}
                                        helperText={editErrors.description || ""}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>

                        <Divider />

                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Betraege & Steuer
                            </Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} sm={4}>
                                    <TextField
                                        label="Betrag brutto"
                                        type="number"
                                        value={editIncome.grossAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateEditIncome({ grossAmount: e.target.value }, "grossAmount");
                                            onClearEditError("grossAmount");
                                            onClearEditError("netAmount");
                                        }}
                                        error={Boolean(editErrors.grossAmount)}
                                        helperText={editErrors.grossAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={4}>
                                    <TextField
                                        label="Betrag netto"
                                        type="number"
                                        value={editIncome.netAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateEditIncome({ netAmount: e.target.value }, "netAmount");
                                            onClearEditError("netAmount");
                                        }}
                                        error={Boolean(editErrors.netAmount)}
                                        helperText={editErrors.netAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sm={4}>
                                    <TextField
                                        label="USt-Betrag"
                                        type="number"
                                        value={editIncome.vatAmount ?? ""}
                                        onChange={(e) => {
                                            onUpdateEditIncome({ vatAmount: e.target.value }, "vatAmount");
                                            onClearEditError("vatAmount");
                                            onClearEditError("netAmount");
                                        }}
                                        error={Boolean(editErrors.vatAmount)}
                                        helperText={editErrors.vatAmount || ""}
                                        fullWidth
                                        inputMode="decimal"
                                        inputProps={{ step: "0.01", min: "0" }}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(editIncome.taxExcludedContribution)}
                                                onChange={(e) =>
                                                    onSetEditIncome({
                                                        ...editIncome,
                                                        taxExcludedContribution: e.target.checked,
                                                    })
                                                }
                                            />
                                        }
                                        label="Als steuerlich nicht zu beruecksichtigende Einlage behandeln"
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>
                    </Stack>
                </Stack>
            ) : (
                <LinearProgress />
            )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} color="inherit" disabled={detailSaving}>
                Schliessen
            </Button>
            <Button
                onClick={onSave}
                disabled={!editIncome?.id || detailSaving}
                variant="contained"
                sx={{ backgroundColor: "orange", color: "#000", fontWeight: 700, "&:hover": { backgroundColor: "#ff9e1a" } }}
            >
                {detailSaving ? "Speichere..." : "Speichern"}
            </Button>
        </DialogActions>
    </Dialog>
);

export default IncomeDetailsDialog;
